/**
 * Language utilities - single source of truth for language handling.
 * These are shared between backend and frontends.
 */
import type { SupportedLanguagesEnum } from "../../service/prisma/client.ts";

/** All supported languages (derived from Prisma enum) */
export type SupportedLanguage = SupportedLanguagesEnum;

/** Languages that can be translated to (all except English) */
export type TranslatableLanguage = Exclude<SupportedLanguage, "en">;

/** Exhaustive map - TS error if Prisma enum changes */
const LANGUAGE_MAP: { [K in SupportedLanguage]: K } = {
  en: "en",
  fr: "fr",
  it: "it",
  de: "de",
  es: "es",
  pt: "pt",
  ar: "ar",
  nl: "nl",
};

export const SUPPORTED_LANGUAGES = Object.values(LANGUAGE_MAP);

/** Array of translatable languages (all except English) */
export const TRANSLATABLE_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (lang): lang is TranslatableLanguage => lang !== "en",
);

/**
 * English names of the supported languages, for LLM prompts ("answer in
 * French"). A model follows "French" far more reliably than a bare "fr".
 */
export const LANGUAGE_ENGLISH_NAMES = {
  en: "English",
  fr: "French",
  it: "Italian",
  de: "German",
  es: "Spanish",
  pt: "Portuguese",
  ar: "Arabic",
  nl: "Dutch",
} as const satisfies Record<SupportedLanguage, string>;

export function buildByLanguage<T>(
  make: (lang: SupportedLanguage) => T,
): Record<SupportedLanguage, T> {
  return {
    en: make("en"),
    fr: make("fr"),
    it: make("it"),
    de: make("de"),
    es: make("es"),
    pt: make("pt"),
    ar: make("ar"),
    nl: make("nl"),
  };
}

/**
 * Localized copy for a user-facing communication (email template, push
 * notification, …). English is the compile-required source of truth (`en: C`);
 * every other language is `C | null`, where `null` means "not translated yet".
 * The mapped type over `TranslatableLanguage` forces every language to have a
 * slot, so a new supported language can never be silently forgotten — you must
 * either translate it or write `null`.
 *
 * This is the single frame for ALL communication copy — do not reintroduce
 * `isFr ? fr : en` ternaries, which silently deliver English to the other six
 * languages with no slot and no signal. See `docs/messaging-and-workflows.md`
 * §i18n and the `translate-missing` skill for the translation rules.
 */
export type CopyTable<C> = { readonly en: C } & {
  readonly [K in TranslatableLanguage]: C | null;
};

/**
 * Resolve the copy for a language, falling back to English when that language
 * is untranslated. `usedFallback` lets the caller surface the gap (e.g. a
 * preview warning) without re-deriving it.
 */
export function resolveCopy<C>(
  table: CopyTable<C>,
  language: SupportedLanguage,
): { copy: C; usedFallback: boolean } {
  const entry = table[language];
  if (entry === null) return { copy: table.en, usedFallback: true };
  return { copy: entry, usedFallback: false };
}

/** Default "Editor's Note" label per playlist language */
export const EDITORS_NOTE_LABEL = {
  en: "Editor's Note",
  fr: "Note de l'éditeur",
  it: "Nota dell'editore",
  de: "Anmerkung des Herausgebers",
  es: "Nota del editor",
  pt: "Nota do editor",
  ar: "ملاحظة المحرر",
  nl: "Noot van de redacteur",
} satisfies Record<SupportedLanguage, string>;

/** Set of all known default labels — used to detect if a label is still a default */
export const EDITORS_NOTE_DEFAULTS = new Set(Object.values(EDITORS_NOTE_LABEL));

/** French elision: "de X" → "d'X" before a vowel or silent H */
function frenchDe(name: string): string {
  return /^[aeiouhyàâéèêëïîôùûüÿæœ]/i.test(name) ? `d'${name}` : `de ${name}`;
}

/** Italian elision: "di X" → "d'X" before a vowel */
function italianDi(name: string): string {
  return /^[aeiouàèéìòù]/i.test(name) ? `d'${name}` : `di ${name}`;
}

/** "{name}'s Note" label per playlist language */
export function userNoteLabel(
  firstName: string,
  language: SupportedLanguage,
): string {
  const templates = {
    en: `${firstName}'s Note`,
    fr: `Note ${frenchDe(firstName)}`,
    it: `Nota ${italianDi(firstName)}`,
    de: /[sßxz]$/u.test(firstName)
      ? `${firstName}' Notiz`
      : `${firstName}s Notiz`,
    es: `Nota de ${firstName}`,
    pt: `Nota de ${firstName}`,
    ar: `ملاحظة ${firstName}`,
    nl: `Notitie van ${firstName}`,
  } satisfies Record<SupportedLanguage, string>;
  return templates[language];
}

/**
 * Resolves actual delivery language for a user in a broadcast.
 * Uses the user's app-level language preference, validated against room.allowedLanguages.
 */
export function resolveDeliveryLanguage(
  user: { language: SupportedLanguage },
  room: string | null,
  allowedLanguages: SupportedLanguage[],
): SupportedLanguage {
  return room === null ||
    allowedLanguages.length === 0 ||
    allowedLanguages.includes(user.language)
    ? user.language
    : "en";
}

/**
 * The languages a Congress Space is authored in: the union of the allowed
 * languages of the rooms it is linked to, with English always present (the
 * base/fallback), ordered EN-first.
 *
 * ONE implementation for BOTH sides, deliberately. Two consumers apply the same
 * rule at different moments and MUST agree:
 *
 *  - `useCongressActiveLanguages` (apps/admin/.../congress-space/congress-languages.ts)
 *    drives the identity + Top Sessions language tabs, and scopes the admin's
 *    card-title submit guard (`resolveCuratorSubmitBlock`).
 *  - `POST /admin/congress-spaces/:spaceId/top-sessions/publish`
 *    (apps/api/src/router/admin/congress-spaces/top-sessions.ts) re-applies that
 *    same card-title rule server-side, because the admin guard is relaxed while
 *    the item is draft.
 *
 * Two independent implementations of "which languages are active" would be free
 * to drift, and the drift surfaces as a publish the admin offers and the server
 * refuses — or, worse, the reverse: an untitled card reaching readers because
 * the server's language set was narrower than the one the admin validated.
 */
export function congressActiveLanguages(
  rooms: readonly { allowedLanguages: readonly SupportedLanguage[] }[],
): SupportedLanguage[] {
  // English is always present: it is the base/fallback language, so a space is
  // always authored in it whatever its rooms allow.
  const active = new Set<SupportedLanguage>(["en"]);
  for (const room of rooms)
    for (const language of room.allowedLanguages) active.add(language);
  // Filtered through SUPPORTED_LANGUAGES rather than returned as insertion
  // order, so the result is EN-first and deterministic — the admin reports the
  // first offending language, and "first" must not depend on room ordering.
  return SUPPORTED_LANGUAGES.filter((language) => active.has(language));
}
